import React, { useState } from "react";
import { Form, Input, Select, message, Spin, Row, Col } from "antd";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import App from "../../Utils/LayoutMobile";

// ── Theme Constants ──
const C_PINK      = "#F04DA0";
const C_PURPLE    = "#8B2FC9";
const C_GRAD      = `linear-gradient(135deg, ${C_PINK}, ${C_PURPLE})`;
const C_SOFT_BG   = "#F8F4FF";
const C_TEXT_MAIN = "#1a0040";
const C_TEXT_MUTED= "#64748B";

// ── Icons ──
const SaveIcon = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
const CancelIcon = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const MessageIcon = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>;

// ── Section Label ──
function SectionLabel({ children }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
      <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.16em", textTransform:"uppercase", color:C_PINK, whiteSpace:"nowrap" }}>
        {children}
      </span>
      <span style={{ flex:1, height:1.5, background:`linear-gradient(90deg, rgba(240,77,160,0.35), transparent)`, borderRadius:2 }}/>
    </div>
  );
}

const CreateFeedback = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/Feedback/feedback`,
        values   // contains feedbackText, not message
      );
      message.success("Feedback added successfully!");
      form.resetFields();
      navigate("/feedback");
    } catch (error) {
      console.error("Frontend Error:", error);
      message.error(error.response?.data?.message || "Failed to add feedback");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <App>
      <div className="lc">
        <div className="mw">
          
          <div className="header-flex">
            <div>
              <SectionLabel>Manual Entry</SectionLabel>
              <h1 className="sec-title">Add <span style={{ color: C_PINK }}>Feedback</span></h1>
              <p className="sec-sub">Manually input a testimonial or review to display on the website.</p>
            </div>
            <div className="icon-wrap"><MessageIcon /></div>
          </div>

          <Spin spinning={submitting}>
            <div className="glass form-wrapper">
              <Form 
                form={form} 
                layout="vertical" 
                onFinish={handleSubmit}
                className="premium-form"
                requiredMark={false}
                initialValues={{ rating: "5" }}
              >
                
                <h3 className="form-section-title">Reviewer Details</h3>
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item name="name" label="Full Name" rules={[{ required: true, message: "Please enter the name" }]}>
                      <Input placeholder="e.g. Dr. Jane Doe" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="email" label="Email Address" rules={[{ required: true, type: "email", message: "Please enter a valid email" }]}>
                      <Input placeholder="e.g. jane@example.com" />
                    </Form.Item>
                  </Col>
                </Row>

                <div className="form-divider" />

                <h3 className="form-section-title">Review Content</h3>
                <Row gutter={24}>
                  <Col xs={24} md={8}>
                    <Form.Item name="rating" label="Rating" rules={[{ required: true }]}>
                      <Select className="custom-select">
                        <Select.Option value="5">⭐⭐⭐⭐⭐ (5 - Excellent)</Select.Option>
                        <Select.Option value="4">⭐⭐⭐⭐ (4 - Good)</Select.Option>
                        <Select.Option value="3">⭐⭐⭐ (3 - Average)</Select.Option>
                        <Select.Option value="2">⭐⭐ (2 - Poor)</Select.Option>
                        <Select.Option value="1">⭐ (1 - Terrible)</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    {/* ← Field renamed from "message" to "feedbackText" */}
                    <Form.Item
                      name="feedbackText"
                      label="Feedback / Testimonial Message"
                      rules={[{ required: true, message: "Please enter the feedback" }]}
                    >
                      <Input.TextArea 
                        rows={5} 
                        placeholder="Type the exact feedback provided by the attendee..." 
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={() => navigate("/feedback")} disabled={submitting}>
                    <CancelIcon /> Cancel
                  </button>
                  <button type="submit" className="btn-cta" disabled={submitting}>
                    <SaveIcon /> Publish Feedback
                  </button>
                </div>

              </Form>
            </div>
          </Spin>

        </div>
      </div>

      <style>{`
        * { box-sizing: border-box; }
        .lc { font-family: 'Segoe UI', sans-serif; background: ${C_SOFT_BG}; min-height: 100vh; padding: 40px 0 80px; }
        .mw { max-width: 800px; margin: 0 auto; padding: 0 24px; }
        .header-flex { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 32px; }
        .sec-title { font-size: clamp(28px, 4vw, 38px); font-weight: 800; color: ${C_TEXT_MAIN}; letter-spacing: -0.02em; line-height: 1.15; margin: 0 0 8px; margin-top: 0; }
        .sec-sub { font-size: 15px; color: ${C_TEXT_MUTED}; margin: 0; }
        .icon-wrap { width: 54px; height: 54px; background: rgba(240,77,160,0.1); border: 1px solid rgba(240,77,160,0.2); border-radius: 16px; display: flex; align-items: center; justify-content: center; color: ${C_PINK}; }
        .glass { background: #fff; border-radius: 20px; padding: 36px 40px; border: 1px solid rgba(139,47,201,0.08); box-shadow: 0 10px 40px rgba(0,0,0,0.04); }
        .form-section-title { font-size: 16px; font-weight: 700; color: ${C_TEXT_MAIN}; margin-top: 0; margin-bottom: 20px; }
        .form-divider { height: 1px; background: rgba(139,47,201,0.08); margin: 32px 0; }
        .premium-form .ant-form-item-label > label { font-weight: 600 !important; color: ${C_TEXT_MAIN} !important; font-size: 13.5px !important; }
        .premium-form .ant-input, .custom-select .ant-select-selector { padding: 12px 16px !important; border-radius: 10px !important; border: 1px solid rgba(139,47,201,0.15) !important; background: #faf6fe !important; color: ${C_TEXT_MAIN} !important; font-size: 14.5px !important; transition: all 0.3s; }
        .premium-form .ant-input:focus, .premium-form .ant-input:hover, .custom-select:hover .ant-select-selector { border-color: ${C_PINK} !important; background: #fff !important; box-shadow: 0 0 0 3px rgba(240,77,160,0.1) !important; }
        .custom-select .ant-select-selector { padding: 5px 16px !important; height: 46px !important; }
        .form-actions { display: flex; align-items: center; justify-content: flex-end; gap: 16px; margin-top: 40px; padding-top: 24px; border-top: 1px solid rgba(139,47,201,0.08); }
        .btn-cta { display: inline-flex; align-items: center; gap: 8px; padding: 14px 28px; background: ${C_GRAD}; border: none; border-radius: 50px; color: #fff; font-size: 14.5px; font-weight: 700; cursor: pointer; box-shadow: 0 10px 24px rgba(240,77,160,0.3); transition: all 0.25s; }
        .btn-cta:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 14px 28px rgba(240,77,160,0.4); }
        .btn-cta:disabled { opacity: 0.7; cursor: not-allowed; }
        .btn-cancel { display: inline-flex; align-items: center; gap: 8px; padding: 14px 24px; background: transparent; border: 1px solid rgba(139,47,201,0.2); border-radius: 50px; color: ${C_TEXT_MUTED}; font-size: 14.5px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
        .btn-cancel:hover:not(:disabled) { background: #faf6fe; color: ${C_TEXT_MAIN}; border-color: rgba(139,47,201,0.4); }
        @media(max-width: 768px) {
          .glass { padding: 24px 20px; border-radius: 16px; }
          .header-flex { flex-direction: column; align-items: flex-start; gap: 16px; }
          .icon-wrap { display: none; }
          .form-actions { flex-direction: column-reverse; align-items: stretch; }
          .btn-cta, .btn-cancel { width: 100%; justify-content: center; }
        }
      `}</style>
    </App>
  );
};

export default CreateFeedback;